import { useCallback, useEffect, useState } from 'react';

import {
  deleteFromStorage,
  getFromStorage,
  saveInStorage,
} from '@/utils/webStorage';

type Options = {
  key: string;
  useSessionStorage?: boolean;
};

function getValueFromStorage<T>(key: string, session = false): T | null {
  const saved = getFromStorage(key, { session });

  if (!saved) {
    return null;
  }

  try {
    const parsed = JSON.parse(saved);
    return parsed as T;
  } catch (e) {
    // Doesn't parse, must just be a string string;
    return saved as T;
  }
}

/**
 * Get and update a value in local storage, will be JSON.stringify()-ed when set
 * and JSON.parse()-ed when read
 * @param key The string that is the key for the local storage item
 * @returns Saved value as well as a function to update local storage
 */
function useWebStorage<T>({ key, useSessionStorage = false }: Options): {
  savedValue: T | null;
  updateValue: (newVal: T) => void;
  deleteValue: () => void;
  isInitialized: boolean;
} {
  const [savedValue, setSavedValue] = useState<T | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  /** Gets the newest value of out storage and saves it */
  const refreshFromStorage = useCallback(() => {
    setSavedValue(getValueFromStorage<T>(key, useSessionStorage));
  }, [key, useSessionStorage]);

  /** Update the value in browser storage */
  const updateValue = useCallback(
    (newValue: T) => {
      saveInStorage(
        key,
        typeof newValue === 'object'
          ? JSON.stringify(newValue)
          : String(newValue),
        { session: useSessionStorage }
      );

      refreshFromStorage();
    },
    [key, useSessionStorage, refreshFromStorage]
  );

  const deleteValue = useCallback(() => {
    deleteFromStorage(key, { session: useSessionStorage });
    refreshFromStorage();
  }, [key, useSessionStorage, refreshFromStorage]);

  useEffect(() => {
    refreshFromStorage();
    setIsInitialized(true);
  }, [refreshFromStorage]);

  return {
    deleteValue,
    isInitialized,
    savedValue,
    updateValue,
  };
}

export default useWebStorage;
